'use client';

/**
 * Video carousel — auto-advancing showcase rail for the {@code /tube}
 * landing surface. Data comes from {@link useTubeVideos}, which reads
 * the host-mounted {@code MediaSource} adapter
 * ({@code media.videos.fetchSource}).
 *
 * <h3>Props</h3>
 *
 * <ul>
 *   <li>{@code title} / {@code subtitle} — section chrome.</li>
 *   <li>{@code source} — feed selector (e.g. {@code featured},
 *       {@code trending}, {@code latest}). Passed through to the
 *       adapter once wired.</li>
 *   <li>{@code limit} — max items in the rail; default 8.</li>
 *   <li>{@code seeAllHref} / {@code seeAllLabel} — optional "see all"
 *       action.</li>
 * </ul>
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@akademi/ui';
import { SectionShell } from '../_shared/SectionShell';
import { EmptyState } from '../_shared/EmptyState';
import { ContentRail } from '../ui/ContentRail';
import { cardReset } from '../_shared/styles';
import { useTubeVideos, type TubeVideo } from './useTubeVideos';

type Localized = string | Record<string, string>;

export interface VideoCarouselProps {
  title?: Localized;
  subtitle?: Localized;
  source?: string;
  limit?: number;
  seeAllLabel?: string;
  seeAllHref?: string;
  /** href template for individual video cards; default
   *  {@code /tube/{slug}}. */
  videoHrefTemplate?: string;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

function fillTemplate(tmpl: string, vars: Record<string, string>): string {
  return tmpl.replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? '');
}

export function VideoCarousel({
  title,
  subtitle,
  source = 'featured',
  limit: rawLimit,
  seeAllLabel,
  seeAllHref,
  videoHrefTemplate = '/tube/{slug}',
}: VideoCarouselProps) {
  const { t } = useTranslation('site-builder-blocks');
  const limit = clamp(
    typeof rawLimit === 'number' && Number.isFinite(rawLimit) ? rawLimit : 8,
    1,
    24,
  );

  const { videos, loading } = useTubeVideos({ source, limit });

  const action: ReactNode = seeAllHref ? (
    <a
      href={seeAllHref}
      style={{
        fontSize: 'var(--font-size-sm, 14px)',
        color: 'var(--color-text-link, var(--color-brand-600, #4338ca))',
        textDecoration: 'none',
      }}
    >
      {seeAllLabel || t('shared.seeAll')} →
    </a>
  ) : null;

  const titleNode: ReactNode = typeof title === 'string' || title === undefined ? title : undefined;

  return (
    <SectionShell
      title={titleNode}
      subtitle={typeof subtitle === 'string' ? subtitle : undefined}
      action={action}
    >
      {loading ? (
        <div
          style={{
            padding: 'var(--space-8, 32px)',
            color: 'var(--color-text-secondary, #64748b)',
          }}
        >
          {t('shared.loading')}
        </div>
      ) : videos.length === 0 ? (
        <EmptyState title={t('videoCarousel.empty.title')} hint={t('videoCarousel.empty.hint')} />
      ) : (
        <ContentRail
          snap="start"
          ariaLabel={typeof title === 'string' ? title : t('shared.label.videos')}
        >
          {videos.map((v) => (
            <VideoTile
              key={v.id}
              video={v}
              href={fillTemplate(videoHrefTemplate, { id: v.id, slug: v.slug ?? v.id })}
            />
          ))}
        </ContentRail>
      )}
    </SectionShell>
  );
}

function VideoTile({ video, href }: { video: TubeVideo; href: string }) {
  return (
    <Card asChild>
      <a
        href={href}
        style={{
          ...cardReset,
          display: 'block',
          width: 320,
          minWidth: 280,
          textDecoration: 'none',
          color: 'inherit',
          borderRadius: 'var(--radius-lg, 12px)',
          overflow: 'hidden',
          background: 'var(--color-surface-card, #fff)',
          transition: 'transform 180ms ease',
        }}
      >
        <div
          style={{
            aspectRatio: '16 / 9',
            background: video.thumbnail
              ? `url("${video.thumbnail}") center/cover no-repeat`
              : 'linear-gradient(135deg, var(--color-neutral-100, #f1f5f9), var(--color-neutral-200, #e2e8f0))',
          }}
        />
        <div style={{ padding: 'var(--space-3, 12px) var(--space-4, 16px) var(--space-4, 16px)' }}>
          <div
            style={{
              fontWeight: 600,
              fontSize: 'var(--font-size-base, 15px)',
              color: 'var(--color-text-primary, #0f172a)',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {video.title}
          </div>
          {video.duration && (
            <div
              style={{
                marginTop: 6,
                fontSize: 'var(--font-size-xs, 12px)',
                color: 'var(--color-text-secondary, #64748b)',
              }}
            >
              {video.duration}
            </div>
          )}
        </div>
      </a>
    </Card>
  );
}
